import { useState } from 'react';
import {
  Button,
  Card,
  Badge,
  Input,
  Modal,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  type Category,
  type CategoryPayload,
} from './useMasterData';
import { formatDate } from '../../shared/utils/format';

/* ── Helpers ── */

const emptyForm: CategoryPayload = { name: '', description: '', active: true };

/* ── Component ── */

export function CategoriesPage(): JSX.Element {
  const { addToast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryPayload>(emptyForm);

  const { data, isLoading, error } = useCategories();

  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();

  const openCreate = (): void => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (category: Category): void => {
    setEditing(category);
    setForm({
      name: category.name,
      description: category.description,
      active: category.active,
    });
    setShowModal(true);
  };

  const handleSave = (): void => {
    if (!form.name) {
      addToast('O nome é obrigatório', 'warning');
      return;
    }

    if (editing) {
      updateMutation.mutate(
        { ...form, id: editing.id },
        {
          onSuccess: () => {
            addToast('Categoria atualizada com sucesso', 'success');
            setShowModal(false);
            setEditing(null);
          },
          onError: (err) => {
            addToast(err instanceof Error ? err.message : 'Erro ao atualizar categoria', 'error');
          },
        },
      );
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          addToast('Categoria criada com sucesso', 'success');
          setShowModal(false);
        },
        onError: (err) => {
          addToast(err instanceof Error ? err.message : 'Erro ao criar categoria', 'error');
        },
      });
    }
  };

  const handleDelete = (id: string): void => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        addToast('Categoria excluída com sucesso', 'success');
        setDeleteConfirmId(null);
      },
      onError: (err) => {
        addToast(err instanceof Error ? err.message : 'Erro ao excluir categoria', 'error');
        setDeleteConfirmId(null);
      },
    });
  };

  return (
    <div className="md-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Categorias</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Categorias de produtos e serviços.</p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button variant="primary" onClick={openCreate}>
          Nova Categoria
        </Button>
        </div>
      </div>

      <Card>
        {isLoading && (
          <div className="loading-container">
            <LoadingSkeleton count={6} height="32px" />
          </div>
        )}

        {error && (
          <div className="error-container">
            <p className="error-message">Erro ao carregar categorias: {(error as Error).message}</p>
          </div>
        )}

        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="empty-container">
            <p className="empty-message">Nenhuma categoria cadastrada.</p>
            <Button variant="outline" size="sm" onClick={openCreate}>
              Cadastrar primeira categoria
            </Button>
          </div>
        )}

        {!isLoading && !error && data && data.length > 0 && (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Descrição</th>
                  <th>Status</th>
                  <th>Criado em</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {data.map((category) => (
                  <tr key={category.id}>
                    <td className="cell-name">{category.name}</td>
                    <td className="cell-desc" title={category.description}>
                      {category.description || '-'}
                    </td>
                    <td>
                      <Badge variant={category.active ? 'success' : 'neutral'}>
                        {category.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td>{formatDate(category.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        <Button size="sm" variant="outline" onClick={() => openEdit(category)}>
                          Editar
                        </Button>
                        {deleteConfirmId === category.id ? (
                          <div className="inline-confirm">
                            <span className="confirm-text">Excluir?</span>
                            <Button
                              size="sm"
                              variant="danger"
                              loading={deleteMutation.isPending}
                              onClick={() => handleDelete(category.id)}
                            >
                              Sim
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirmId(null)}
                              disabled={deleteMutation.isPending}
                            >
                              Não
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeleteConfirmId(category.id)}
                          >
                            Excluir
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        title={editing ? 'Editar Categoria' : 'Nova Categoria'}
        footer={
          <div className="modal-actions">
            <Button variant="ghost" onClick={() => { setShowModal(false); setEditing(null); }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!form.name || createMutation.isPending || updateMutation.isPending}
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Atualizar' : 'Salvar'}
            </Button>
          </div>
        }
      >
        <div className="modal-form">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Nome da categoria"
          />
          <Input
            label="Descrição"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Descrição da categoria"
          />
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span>Ativo</span>
          </label>
          {(createMutation.isError || updateMutation.isError) && (
            <p className="field-error">
              {((createMutation.error ?? updateMutation.error) as Error)?.message ?? ''}
            </p>
          )}
        </div>
      </Modal>

      <style>{`
        .md-page { padding: 1.5rem; }
        .loading-container { padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
        .error-container { padding: 2rem; text-align: center; }
        .error-message { color: #ba1a1a; font-size: 0.875rem; }
        .empty-container { padding: 2rem; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem; }
        .empty-message { color: #74777f; font-size: 0.875rem; margin: 0; }
        .table-wrapper { overflow-x: auto; }
        .data-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .data-table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .data-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .data-table tbody tr:hover td { background: #eceef0; }
        .cell-name { font-weight: 600; }
        .cell-desc { max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #74777f; }
        .row-actions { display: flex; gap: 0.375rem; align-items: center; }
        .inline-confirm { display: flex; align-items: center; gap: 0.375rem; }
        .confirm-text { font-size: 0.8125rem; color: #ba1a1a; font-weight: 600; margin-right: 0.25rem; }
        .modal-form { display: flex; flex-direction: column; gap: 1rem; }
        .modal-actions { display: flex; gap: 0.5rem; }
        .field-error { color: #ba1a1a; font-size: 0.8125rem; margin: 0; }
        .toggle-label { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #191c1e; cursor: pointer; }
        .toggle-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
      `}</style>
    </div>
  );
}
